import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { routes } from '../routes/routes';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from '../../api.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CommonService } from '../common/common.service';

@Component({
  standalone: true,
  selector: 'app-add-alternate-number',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
  ],
  templateUrl: './add-alternate-number.component.html',
  styleUrls: ['./add-alternate-number.component.scss'],
})
export class AddAlternateNumberComponent implements OnInit {
  @Output() triggerMoveToHome: EventEmitter<any> = new EventEmitter<any>();
  @Output() triggerOtpSuccess: EventEmitter<any> = new EventEmitter<any>();
  @Input() handleDirectShowOtp: boolean = false;
  @Input() phoneNumber: any;
  public routes = routes;
  phoneForm: any;
  contactNumberError: boolean = false;
  showOTP: boolean = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];
  countdown: number = 300;
  displayTime: string = '5 min : 00 sec';
  maskedPhone: string = '';
  public oneTimePassword: any = {
    data1: '',
    data2: '',
    data3: '',
    data4: '',
    data5: '',
    data6: '',
  };
  public isSubmitted: boolean = false;
  originaleNumber: any;
  public errorMessage: string = '';
  public errorvalidOtp: string = '';
  interval: any;
  alternateNoShow: boolean = true;
  timerInterval: any = null;
  userProfile: any;

  constructor(
    private fb: FormBuilder,
    private spinnerService: NgxSpinnerService,
    private apiService: ApiService,
    private tosterService: ToastrService,
    private router: Router,
    private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.userProfile = JSON.parse(localStorage.getItem('user') || '{}');
    this.phoneForm = this.fb.group({
      phone: ['', [Validators.required, this.zimbabweMobileValidator()]],
    });

    this.phoneForm.get('phone')?.valueChanges.subscribe(() => {
      this.phoneForm.get('phone')?.updateValueAndValidity({ emitEvent: false });
    });

    if (this.handleDirectShowOtp) {
      this.showOTP = true;
      this.originaleNumber = this.phoneNumber;
      this.resetOtpFields();
      this.startOtpTimer();
      this.maskedPhone = this.maskPhoneNumber(this.phoneNumber);
      this.alternateNoShow = false;
    }
  }

  zimbabweMobileValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value: string = control?.value?.number || '';

      if (!value) return { invalidZimbabweMobile: true };

      value = value.replace(/\s+/g, '');

      if (value.startsWith('0') && value.length === 10) {
        return null;
      }

      if (value.startsWith('+263') && value.length === 13) {
        return null;
      }

      if (
        !value.startsWith('0') &&
        !value.startsWith('+263') &&
        value.length === 9
      ) {
        return null;
      }

      return { invalidZimbabweMobile: true };
    };
  }

  validateLoginNumber(ev: any) {
    const rawValue = ev?.target?.value || '';
    const onlyDigits = rawValue.replace(/\D/g, '');
    ev.target.value = onlyDigits;
    const expectedLength = onlyDigits.startsWith('0') ? 10 : 9;
    this.contactNumberError = onlyDigits.length !== expectedLength;
  }

  blockNonNumeric(event: any) {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
    ];

    if (allowedKeys.includes(event.key)) return;

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const inputValue = (event.target as HTMLInputElement).value;
    const digits = inputValue.replace(/\D/g, '');

    const cursorPos = (event.target as HTMLInputElement).selectionStart || 0;
    const expectedLength = digits.startsWith('0') ? 10 : 9;

    if (digits.length >= expectedLength && cursorPos === digits.length) {
      event.preventDefault();
    }
  }

  public allowOnlyNumbers(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
    ];

    if (allowedKeys.includes(event.key)) {
      this.errorMessage = '';
      return;
    }

    if (event.key === 'Unidentified' || event.key.length > 1) {
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      this.errorMessage = 'Only numbers (0–9) are allowed';
    }
  }

  public tiggerBackspace(data: string, box: string): void {
    if (data) return;

    const current = parseInt(box.split('-')[1], 10);
    const prev = current - 1;

    if (prev >= 1) {
      document.getElementById(`digit-${prev}`)?.focus();
    }
  }

  onOtpInput(ev: any, currentIndex: number): void {
    let value = ev.target.value || '';

    value = value.replace(/\D/g, '').slice(-1);
    ev.target.value = value;
    this.oneTimePassword['data' + currentIndex] = value;

    if (value && currentIndex < 6) {
      const nextBox = document.getElementById(`digit-${currentIndex + 1}`);
      nextBox?.focus();
    }
  }

  public validateOtp(): void {
    const otpValues = Object.values(this.oneTimePassword).join('');
    if (otpValues.length === 6 && /^[0-9]{6}$/.test(otpValues)) {
      this.errorMessage = '';
    }
  }

  handlePaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.trim().replace(/\D/g, '').split('');
    if (digits.length > 0) {
      digits.slice(0, 6).forEach((digit, index) => {
        this.oneTimePassword['data' + (index + 1)] = digit;
        const nextInput = document.getElementById(
          `digit-${index + 1}`,
        ) as HTMLElement;
        nextInput?.focus();
      });
    }
    event.preventDefault();
  }

  public sendOtp() {
    this.spinnerService.show();
    let phone = this.phoneForm.value.phone?.number?.replace(/\s/g, '') || '';

    if (phone.startsWith('0') && phone.length === 10) {
      phone = phone.slice(1);
    } else if (phone.startsWith('+263') && phone.length === 13) {
      phone = phone.slice(4);
    }

    if (phone.length !== 9 || this.contactNumberError) {
      this.phoneForm.markAllAsTouched();
      this.spinnerService.hide();
      return;
    }

    this.originaleNumber = phone;

    const payload = {
      phone_number: phone,
      user_id: this.userProfile.user_id,
      otp_type: 'ECOCASH',
    };

    this.apiService.sendEcocashOtp(payload).subscribe(
      ({ success }) => {
        if (success) {
          this.tosterService.success('OTP sent successfully', '', {
            timeOut: 4000,
          });

          this.showOTP = true;
          this.alternateNoShow = false;
          this.resetOtpFields();
          this.startOtpTimer();
          this.maskedPhone = this.maskPhoneNumber(phone);

          localStorage.setItem('auth', JSON.stringify({ phone }));
        } else {
          this.tosterService.error(
            'OTP sending failed. Please try again.',
            '',
            { timeOut: 4000 },
          );
          setTimeout(() => {
            this.spinnerService.hide();
          }, 1000);
        }

        this.spinnerService.hide();
      },
      (error) => {
        console.error('API Error:', error.error);
        setTimeout(() => {
          this.spinnerService.hide();
        }, 2000);

        const errorMessage =
          error?.error?.error ||
          error?.message ||
          error?.error?.message ||
          'Something went wrong.';

        this.tosterService.error(errorMessage, '', { timeOut: 4000 });
      },
    );
  }

  private resetOtpFields() {
    this.oneTimePassword = {
      data1: '',
      data2: '',
      data3: '',
      data4: '',
      data5: '',
      data6: '',
    };

    this.errorMessage = '';
    this.isSubmitted = false;
  }

  startOtpTimer() {
    this.countdown = 300;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.updateDisplayTime();

    this.timerInterval = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
        this.updateDisplayTime();
      } else {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }, 1000);
  }

  validateOTP(): void {
    this.isSubmitted = true;
    this.errorvalidOtp = '';
    this.spinnerService.show();

    const otpValues = Object.values(this.oneTimePassword);
    if (otpValues.some((v) => !v)) {
      this.errorMessage = 'Please enter a valid OTP.';
      this.spinnerService.hide();
      return;
    }

    this.isSubmitted = false;
    const otp = otpValues.join('');
    const phone =
      this.originaleNumber && this.originaleNumber.replace(/\s/g, '');
    const payload = {
      otp: otp,
      phone: phone,
    };

    this.apiService.otpVerification(payload).subscribe({
      next: ({ success }) => {
        this.spinnerService.hide();
        if (!success) {
          this.tosterService.error(
            'OTP verification failed. Please try again.',
            '',
            { timeOut: 4000 },
          );
          return;
        }

        const userId = this.userProfile.user_id;

        this.apiService.getUserProfileRecord(userId).subscribe(
          (res: any) => {
            this.userProfile = res.data || {};
            localStorage.setItem('user', JSON.stringify(this.userProfile));
            this.commonService.updateProfile();
          },
          (err: any) => {
            console.error('Error fetching profile:', err);
          },
        );
        this.resetOtpFields();
        this.triggerOtpSuccess.emit(phone);
        this.showOTP = false;
        this.alternateNoShow = false;
        localStorage.removeItem('auth');

        this.tosterService.success('OTP successfully verified', '', {
          timeOut: 4000,
        });
      },
      error: (error) => {
        this.spinnerService.hide();
        const errMsg =
          error?.error?.error ||
          error?.message ||
          error?.error?.message ||
          'An error occurred. Please try again.';

        this.tosterService.error(errMsg, '', { timeOut: 4000 });
      },
    });
  }

  updateDisplayTime() {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;

    this.displayTime = `${minutes} min : ${seconds < 10 ? '0' : ''}${seconds}`;
  }

  clearOtpInputs() {
    for (let i = 1; i <= 6; i++) {
      this.oneTimePassword['data' + i] = '';
    }
  }

  closePopup() {
    this.alternateNoShow = false;
    this.triggerMoveToHome.emit(true);
  }

  closeOTPPopup() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.resetOtpFields();
    this.showOTP = false;
    this.alternateNoShow = true;

    const phoneCtrl = this.phoneForm.get('phone');

    if (phoneCtrl?.value) {
      const cleanedValue =
        typeof phoneCtrl.value === 'string'
          ? phoneCtrl.value.replace(/\s+/g, '')
          : phoneCtrl.value?.number?.replace(/\s+/g, '');

      phoneCtrl.setValue(cleanedValue, { emitEvent: false });
    }

    phoneCtrl?.markAsUntouched();
  }

  maskPhoneNumber(phone: string): string {
    phone = phone.replace(/\s+/g, '');
    if (phone.length > 3) {
      return '******' + phone.slice(-4);
    }
    return phone;
  }

  resendOtp() {
    this.spinnerService.show();

    this.apiService.otpRersend({ phone: this.originaleNumber }).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.tosterService.success('OTP sent successfully', '', {
          timeOut: 4000,
        });
        this.clearOtpInputs();
        this.startOtpTimer();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.tosterService.error(
          error?.error?.error || 'Failed to resend OTP',
          '',
          { timeOut: 4000 },
        );
      },
    });
  }
}
